import { useQueryClient } from '@tanstack/react-query';
import { FC, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { CloseButton, StyledDialog } from '@/components/AlertDialogs/Dialog';
import Button from '@/components/Button';
import { Textarea } from '@/components/Forms';
import { Modal } from '@/components/Modal';
import { SidePanelFooter } from '@/components/SidePanel';
import Text from '@/components/Text';
import useToasts from '@/hooks/useToasts';
import { api } from '@/utils/api';

const Dialog = styled(StyledDialog)`
  width: 600px;
  padding: 0;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  border-radius: 4px;
  padding: 24px;
  position: relative;
  box-shadow: 0px 2px 8px rgba(34, 34, 34, 0.06);
`;

const TextAreaHolder = styled.div`
  padding: 24px;
  padding-bottom: 56px;
`;

const StyledTextarea = styled(Textarea)`
  min-height: 310px;
`;

interface Props {
  onClose: () => void;
  setSelectedImages: React.Dispatch<React.SetStateAction<number[]>>;
  comment?: string;
}

const CommentModal: FC<Props> = ({ onClose, setSelectedImages, comment }) => {
  const [isPosting, setIsPosting] = useState(false);

  const { id } = useParams();

  const queryClient = useQueryClient();

  const toasts = useToasts();

  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const onPostComment = async () => {
    const toastId = toasts.loading();
    setIsPosting(true);
    try {
      await api.post(`/v1/cover-images/general-comment/${id}`, {
        comment: textAreaRef.current?.value,
      });

      await queryClient.invalidateQueries(['cover_images_project', { id }], {
        exact: true,
      });

      toasts.success(toastId);
      setSelectedImages([]);

      onClose();
    } catch (_) {
      toasts.error(toastId);
    }
    setIsPosting(false);
  };

  return (
    <Modal zIndex={1}>
      <Dialog
        initial={{ scale: 0.5 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.5 }}
      >
        <ModalHeader>
          <Text $type="button">Leave a comment · General</Text>
          <CloseButton onClick={onClose}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13 1L1 13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13 13L1 1"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </CloseButton>
        </ModalHeader>
        <TextAreaHolder>
          <StyledTextarea
            disabled={isPosting}
            ref={textAreaRef}
            placeholder="Add comment"
            defaultValue={comment}
          ></StyledTextarea>
        </TextAreaHolder>
        <SidePanelFooter $actions>
          <Button type="button" disabled={isPosting} onClick={onPostComment}>
            Save
          </Button>
          <Button
            color="secondary"
            disabled={isPosting}
            onClick={onClose}
            type="button"
          >
            Cancel
          </Button>
        </SidePanelFooter>
      </Dialog>
    </Modal>
  );
};

export default CommentModal;
